/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionRequest, ActionSuccess, ActionFail, XError } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, takeEvery, take, race } from 'redux-saga/effects';
import Constants from 'configs/Constants';
import { StoreState } from 'store/store';
import * as MyTvSecondLevel from './myTvSecondLevel';

enum ActionType {
  GET_THIRD_LEVEL_ASSETS = 'myTV/thirdLevel/GET_THIRD_LEVEL_ASSETS',
  GET_THIRD_LEVEL_ASSETS_SUCCESS = 'myTV/thirdLevel/GET_THIRD_LEVEL_ASSETS_SUCCESS',
  GET_THIRD_LEVEL_ASSETS_FAIL = 'myTV/thirdLevel/GET_THIRD_LEVEL_ASSETS_FAIL',
}

interface GetThirdLevelAssetsMeta {
  railId: string;
  page: number;
  menuItemId: number;
}

interface GetThirdLevelAssetsPayload {
  data: MyTvSecondLevel.SecondLevelRails;
  totalAssets: number;
}

type GetThirdLevelAssetsRequest = ActionRequest<ActionType.GET_THIRD_LEVEL_ASSETS, GetThirdLevelAssetsMeta>;
type GetThirdLevelAssetsSuccess = ActionSuccess<ActionType.GET_THIRD_LEVEL_ASSETS_SUCCESS, GetThirdLevelAssetsPayload>;
type GetThirdLevelAssetsFail = ActionFail<ActionType.GET_THIRD_LEVEL_ASSETS_FAIL, GetThirdLevelAssetsMeta>;

export type Actions = GetThirdLevelAssetsRequest | GetThirdLevelAssetsSuccess | GetThirdLevelAssetsFail;

const Actions = {
  getThirdLevelAssets: (railId: string, page: number, menuItemId: number): GetThirdLevelAssetsRequest => ({
    type: ActionType.GET_THIRD_LEVEL_ASSETS,
    meta: {
      railId,
      page,
      menuItemId,
    },
  }),
  getThirdLevelAssetsSuccess: (
    secondLevelRails: MyTvSecondLevel.SecondLevelRails,
    totalAssets: number
  ): GetThirdLevelAssetsSuccess => ({
    type: ActionType.GET_THIRD_LEVEL_ASSETS_SUCCESS,
    payload: {
      data: secondLevelRails,
      totalAssets,
    },
  }),
  getThirdLevelAssetsFail: (
    railId: string,
    page: number,
    menuItemId: number,
    error: XError
  ): GetThirdLevelAssetsFail => ({
    type: ActionType.GET_THIRD_LEVEL_ASSETS_FAIL,
    error,
    meta: { railId, page, menuItemId },
  }),
};

export const ActionCreators = {
  getThirdLevelAssets: (railId: string, page: number, menuItemId: number): GetThirdLevelAssetsRequest =>
    Actions.getThirdLevelAssets(railId, page, menuItemId),
};
export interface ReducerState {
  data?: MyTvSecondLevel.SecondLevelRails;
  totalAssets?: number;
  error?: XError;
}

const initialState: ReducerState = {};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_THIRD_LEVEL_ASSETS_SUCCESS:
      return {
        data: action.payload.data,
        totalAssets: action.payload.totalAssets,
        error: undefined,
      };
    case ActionType.GET_THIRD_LEVEL_ASSETS_FAIL:
      return {
        data: {
          assets: [],
          menuItemId: 0,
          railName: '',
          railId: '',
        },
        totalAssets: 0,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getThirdLevelAssets: (state: StoreState): MyTvSecondLevel.SecondLevelRails | undefined =>
    state.myTv.thirdLevelRails?.data,
  getThirdLevelAssetsNumber: (state: StoreState): number | undefined => state.myTv.thirdLevelRails?.totalAssets,
  getThirdLevelError: (state: StoreState): boolean => !!state.myTv.thirdLevelRails?.error,
};

/* Helping functions */
function* getThirdLevelAssetsSaga(action: GetThirdLevelAssetsRequest): SagaIterator {
  const { railId, page, menuItemId } = action.meta;

  try {
    yield put(MyTvSecondLevel.ActionCreators.getSecondLevelAssets(menuItemId));

    const { error: loadSecondLevelFail } = yield race({
      success: take(MyTvSecondLevel.ActionType.GET_SECOND_LEVEL_ASSETS_SUCCESS),
      error: take(MyTvSecondLevel.ActionType.GET_SECOND_LEVEL_ASSETS_FAIL),
    });

    if (loadSecondLevelFail) {
      yield put(Actions.getThirdLevelAssetsFail(railId, page, menuItemId, loadSecondLevelFail));
      return;
    }

    const secondLevelRails = yield select(MyTvSecondLevel.Selectors.getSecondLevelAssets);
    const rail: MyTvSecondLevel.SecondLevelRails[] = secondLevelRails.filter(
      (item: MyTvSecondLevel.SecondLevelRails) => {
        return item.railId === railId;
      }
    );

    const railSize = rail[0].assets.length;

    const slicedRail = {
      ...rail[0],
      assets: rail[0].assets.slice(0, Constants.ASSETS_PER_GRID_PAGE * page),
    };
    yield put(Actions.getThirdLevelAssetsSuccess(slicedRail, railSize));
  } catch (error) {
    yield put(Actions.getThirdLevelAssetsFail(railId, page, menuItemId, XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionType.GET_THIRD_LEVEL_ASSETS, getThirdLevelAssetsSaga)]);
}
